import { useState, useEffect, useRef } from 'react';
import { McgJazzLogo } from './McgJazzLogo';
import '../styles/ConversationSidebar.css';

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayMidnight - dateMidnight) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

export const ConversationSidebar = ({
  isOpen,
  onToggle,
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onSearchConversations,
  username,
  onLogout,
  showAdminEntry = false,
  onOpenAdmin,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [contentMatches, setContentMatches] = useState({}); // { conversation_id: snippet }
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const q = searchQuery.trim();

    // Clear content matches when query is too short
    if (q.length < 2) {
      setContentMatches({});
      setSearching(false);
      return;
    }

    setSearching(true);

    // Debounce: wait 350ms after user stops typing before hitting the API
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const matches = await onSearchConversations?.(q) ?? [];
      const map = {};
      for (const m of matches) {
        map[m.conversation_id] = m.snippet;
      }
      setContentMatches(map);
      setSearching(false);
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, onSearchConversations]);

  // Build the filtered list: title match OR content match
  const filtered = searchQuery.trim()
    ? conversations.filter((c) => {
        const titleMatch = (c.title || 'New Conversation')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const contentMatch = !!contentMatches[c.conversation_id];
        return titleMatch || contentMatch;
      })
    : conversations;

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && <div className="Sidebar-overlay" onClick={onToggle} />}

      <aside className={`Sidebar ${isOpen ? 'Sidebar--open' : 'Sidebar--closed'}`}>
        <div className="Sidebar-header">
          <div className="Sidebar-brand">
            <McgJazzLogo size={24} color="#C8102E" />
            <span className="Sidebar-title">MCG Jazz</span>
          </div>
          <button className="Sidebar-toggle" onClick={onToggle} aria-label="Close sidebar">
            ✕
          </button>
        </div>

        <button className="Sidebar-newChat" onClick={onNewConversation}>
          + New conversation
        </button>

        {/* Search */}
        <div className="Sidebar-search">
          <input
            className="Sidebar-searchInput"
            type="text"
            placeholder="Search titles & messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="Sidebar-searchClear"
              onClick={() => {
                setSearchQuery('');
                setContentMatches({});
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {/* Searching indicator */}
        {searching && searchQuery.trim().length >= 2 && (
          <p className="Sidebar-searchStatus">Searching messages…</p>
        )}

        <div className="Sidebar-conversations">
          {filtered.length === 0 ? (
            <p className="Sidebar-empty">
              {searchQuery ? 'No results found' : 'No conversations yet'}
            </p>
          ) : (
            filtered.map((conv) => {
              const snippet = contentMatches[conv.conversation_id];
              const titleMatch = searchQuery.trim() &&
                (conv.title || 'New Conversation')
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase());
              // Show snippet only when this is a content-only match (not title match)
              const showSnippet = snippet && !titleMatch;

              return (
                <div
                  key={conv.conversation_id}
                  className={`Sidebar-item ${conv.conversation_id === activeConversationId ? 'Sidebar-item--active' : ''}`}
                  onClick={() => onSelectConversation(conv.conversation_id)}
                >
                  <div className="Sidebar-item-body">
                    <span className="Sidebar-item-title">
                      {conv.title || 'New Conversation'}
                    </span>
                    {showSnippet ? (
                      <span className="Sidebar-item-snippet">{snippet}</span>
                    ) : (
                      <span className="Sidebar-item-date">{formatDate(conv.updated_at ?? conv.created_at)}</span>
                    )}
                  </div>
                  <button
                    className="Sidebar-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteConversation(conv.conversation_id);
                    }}
                    aria-label="Delete conversation"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="Sidebar-footer">
          {showAdminEntry && onOpenAdmin && (
            <button type="button" className="Sidebar-admin" onClick={onOpenAdmin}>
              Admin portal
            </button>
          )}
          <div className="Sidebar-user">
            <span className="Sidebar-username">{username}</span>
            <button className="Sidebar-logout" onClick={onLogout}>
              Sign out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
